import { Link } from '@inertiajs/react';
import { useEffect, useRef, useState } from 'react';
const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

const CONSENT_KEY = 'cookie_consent';

declare global {
    interface Window {
        enableAnalytics?: () => void;
        ga_disabled?: boolean;
        gtag?: (...args: unknown[]) => void;
    }
}

export default function CookieConsent() {
    const [visible, setVisible] = useState(false);
    const hasEnabledAnalytics = useRef(false);

    const updateGoogleConsent = (analytics: boolean) => {
        window.gtag?.('consent', 'update', {
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            analytics_storage: analytics ? 'granted' : 'denied',
        });
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const stored = window.localStorage.getItem(CONSENT_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored) as { analytics?: boolean };
                const allowed = parsed.analytics === true;
                updateGoogleConsent(allowed);
                if (allowed && !hasEnabledAnalytics.current) {
                    hasEnabledAnalytics.current = true;
                    window.ga_disabled = false;
                    window.enableAnalytics?.();
                }
            } catch {
                // Ignore invalid stored values and show banner.
            }
        }
        setVisible(!stored);
    }, []);

    const persistConsent = (analytics: boolean) => {
        if (typeof window === 'undefined') return;
        const payload = {
            necessary: true,
            analytics,
            marketing: false,
        };

        window.localStorage.setItem(CONSENT_KEY, JSON.stringify(payload));
        setVisible(false);

        updateGoogleConsent(analytics);
        window.ga_disabled = !analytics;
        if (analytics && !hasEnabledAnalytics.current) {
            hasEnabledAnalytics.current = true;
            window.enableAnalytics?.();
        }

        window.dispatchEvent(new CustomEvent('cookie_consent_updated'));
    };

    if (!visible) return null;

    return (
        <div className="fixed right-4 bottom-4 left-4 z-[4000]">
            <div className="mx-auto max-w-5xl rounded-xl border border-[oklch(0.2_0_0)] bg-[oklch(0.145_0_0)] p-4 text-sm text-gray-200 shadow-lg sm:flex sm:items-center sm:justify-between sm:gap-4">
                <div className="mb-3 sm:mb-0">
                    <p>
                        We use essential cookies to ensure basic site functionality. By accepting, you also allow analytics cookies to help us improve
                        the site.
                    </p>
                    <Link href="/cookies" className="mt-2 inline-block text-white underline underline-offset-4 hover:text-gray-100">
                        Learn more
                    </Link>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                    <button
                        type="button"
                        onClick={() => persistConsent(false)}
                        className="rounded-lg border border-[oklch(0.24_0_0)] px-4 py-2 text-gray-200 transition hover:bg-[oklch(0.2_0_0)]"
                    >
                        Decline
                    </button>
                    <button
                        type="button"
                        onClick={() => persistConsent(true)}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-4 py-2 font-semibold transition"
                    >
                        Accept
                    </button>
                </div>
            </div>
        </div>
    );
}
